import type { SocialNetwork, SocialEntryKind } from "../global";
import { classifySocialUrl } from "./identify-report-type.helpers";

// `report_search_args` for `redes_sociais`. Two shapes exist:
//   - DISPLAY/returned (what `formatSearchArgs` now builds + what the backend
//     returns): { redes_sociais: "<url|handle>", redes_sociais_ignore: {network,
//     kind, input_mode} } — mirrors the other report types + a filtered key.
//   - Legacy creation object: { redes_sociais: { network, kind, url } } — still
//     produced by the `build*SearchArgs` builders below and handled for
//     backward compatibility by the extractors.
// The whole object is encrypted into `report_input_encrypted`. The ngram index
// includes the ENTRY VALUE (handle/url) + network/kind so the report is
// searchable by its input, like every other type. Mirrors the backend helper
// `domains/reports/redes_sociais_search_args.py`.

export const REDES_SOCIAIS_REPORT_TYPE = "redes_sociais" as const;

/** Fields safe to index (HMAC/ngrams); `url` is excluded. */
export const REDES_SOCIAIS_SAFE_INDEX_FIELDS = ["network", "kind"] as const;

/** URL-derived inner payload. */
export interface RedesSociaisUrlSearchArgsInner {
  network: SocialNetwork;
  kind: SocialEntryKind;
  url: string;
  /** Extracted identifier for `perfil` URLs; omitted for `post`. */
  identifier?: string;
}

/** Identifier-derived inner payload. */
export interface RedesSociaisIdentifierSearchArgsInner {
  network: SocialNetwork;
  kind: "perfil";
  identifier: string;
}

/** Discriminated inner payload: distinguishable by presence of `url`. */
export type RedesSociaisSearchArgsInner =
  | RedesSociaisUrlSearchArgsInner
  | RedesSociaisIdentifierSearchArgsInner;

/** Canonical structured `report_search_args` for `redes_sociais`. */
export interface RedesSociaisSearchArgs {
  redes_sociais: RedesSociaisSearchArgsInner;
}

/** URL_Payload iff the inner object carries a string `url`. */
export function isUrlPayloadInner(
  inner: unknown,
): inner is RedesSociaisUrlSearchArgsInner {
  return typeof (inner as { url?: unknown } | null)?.url === "string";
}

/** Platform-route path segments skipped when extracting a profile identifier. */
const RESERVED_PATH_SEGMENTS: Record<SocialNetwork, ReadonlySet<string>> = {
  instagram: new Set(["explore", "accounts", "directory", "about", "developer"]),
  facebook: new Set([
    "profile.php",
    "people",
    "pages",
    "pg",
    "groups",
    "events",
    "public",
  ]),
  twitter: new Set([
    "i",
    "home",
    "explore",
    "search",
    "hashtag",
    "settings",
    "messages",
    "notifications",
  ]),
  linkedin: new Set([
    "in",
    "company",
    "pub",
    "school",
    "feed",
    "posts",
    "pulse",
    "jobs",
  ]),
};

/**
 * Extract the profile identifier from a normalized Social_URL. `null` for `post`
 * URLs and `perfil` paths with no usable segment (e.g. Facebook `profile.php`).
 */
export function extractIdentifierFromSocialUrl(
  network: SocialNetwork,
  entryKind: SocialEntryKind,
  normalizedUrl: string,
): string | null {
  if (entryKind === "post") return null;

  let pathname: string;
  try {
    pathname = new URL(normalizedUrl).pathname;
  } catch {
    return null;
  }

  const reserved = RESERVED_PATH_SEGMENTS[network];
  const segments = pathname.split("/").filter(Boolean);

  for (const segment of segments) {
    if (!reserved.has(segment.toLowerCase())) return segment;
  }
  return null;
}

/** True only for the canonical `redes_sociais` report_type. */
export function isRedesSociaisType(reportType: string | null | undefined): boolean {
  return reportType === REDES_SOCIAIS_REPORT_TYPE;
}

/** Build the URL payload, extracting the identifier for `perfil` URLs. */
export function buildRedesSociaisUrlSearchArgs(input: {
  network: SocialNetwork;
  entryKind: SocialEntryKind;
  normalizedUrl: string;
}): RedesSociaisSearchArgs {
  const identifier = extractIdentifierFromSocialUrl(
    input.network,
    input.entryKind,
    input.normalizedUrl,
  );
  const inner: RedesSociaisUrlSearchArgsInner = {
    network: input.network,
    kind: input.entryKind,
    url: input.normalizedUrl,
  };
  if (identifier !== null) inner.identifier = identifier;
  return { redes_sociais: inner };
}

/** Build the identifier payload (always `kind: "perfil"`) from a handle + network. */
export function buildRedesSociaisIdentifierSearchArgs(input: {
  network: SocialNetwork;
  identifier: string;
}): RedesSociaisSearchArgs {
  return {
    redes_sociais: {
      network: input.network,
      kind: "perfil",
      identifier: input.identifier,
    },
  };
}

/** Legacy alias of {@link buildRedesSociaisUrlSearchArgs}. */
export const buildRedesSociaisSearchArgs = buildRedesSociaisUrlSearchArgs;

/**
 * Canonical DISPLAY `report_search_args` for `redes_sociais` — the same shape
 * the backend returns (`build_display_search_args`) so the pending card (built
 * from the creation-time args) matches the completed report:
 *
 *   { redes_sociais: "<url|handle>",
 *     redes_sociais_ignore: { network, kind, input_mode } }
 *
 * The main key carries the plaintext entry value (URL in URL mode, handle in
 * identifier mode) like every other report type; the `_ignore` key carries the
 * non-display metadata (filtered by `searchItems` / the details header). The
 * whole object is still envelope-encrypted into `report_input_encrypted`.
 */
export interface RedesSociaisDisplaySearchArgs {
  redes_sociais: string;
  redes_sociais_ignore: {
    network: string;
    kind: string;
    input_mode: "url" | "identifier";
  };
}

export function buildRedesSociaisDisplaySearchArgs(input: {
  value: string;
  network: string;
  kind: string;
  inputMode: "url" | "identifier";
}): RedesSociaisDisplaySearchArgs {
  return {
    [REDES_SOCIAIS_REPORT_TYPE]: input.value,
    [REDES_SOCIAIS_IGNORE_KEY]: {
      network: input.network,
      kind: input.kind,
      input_mode: input.inputMode,
    },
  };
}

/** Key carrying the non-display metadata in the RETURNED envelope shape. */
export const REDES_SOCIAIS_IGNORE_KEY = `${REDES_SOCIAIS_REPORT_TYPE}_ignore` as const;

/**
 * Extract only the safe fields (`network`, `kind`) for indexing; NEVER the url.
 *
 * Handles both shapes:
 *   - RETURNED/display shape: `{ redes_sociais: "<url>", redes_sociais_ignore:
 *     { network, kind, input_mode } }` — read from the `_ignore` key.
 *   - Legacy creation shape: `{ redes_sociais: { network, kind, url } }` — read
 *     from the inner object (backward compatible with reports already stored).
 */
export function extractRedesSociaisSafeFields(
  searchArgs: unknown,
): { network: string; kind: string } {
  const record = searchArgs as Record<string, unknown> | null | undefined;

  const ignore = record?.[REDES_SOCIAIS_IGNORE_KEY];
  if (ignore && typeof ignore === "object" && !Array.isArray(ignore)) {
    const i = ignore as Record<string, unknown>;
    return {
      network: typeof i.network === "string" ? i.network : "",
      kind: typeof i.kind === "string" ? i.kind : "",
    };
  }

  const inner = record?.[REDES_SOCIAIS_REPORT_TYPE];
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const i = inner as Record<string, unknown>;
    return {
      network: typeof i.network === "string" ? i.network : "",
      kind: typeof i.kind === "string" ? i.kind : "",
    };
  }
  return { network: "", kind: "" };
}

/**
 * Extract the plaintext ENTRY VALUE (the handle/URL the user searched by) from
 * either shape, so `redes_sociais` is searchable by its input like every other
 * report type indexes its value (e.g. `cpf` indexes the CPF number).
 *
 *   - RETURNED/display shape: `{ redes_sociais: "<url|handle>", ... }` — the
 *     plaintext string under `redes_sociais`.
 *   - Legacy creation shape: `{ redes_sociais: { url?, identifier?, ... } }` —
 *     prefer `url` (URL mode), else `identifier` (identifier mode). This matches
 *     the value the display/name path uses, so creation and completion index the
 *     same token.
 *
 * The encrypted-url envelope object (`{encrypted, iv}`) is never a plain string,
 * so it is never returned here.
 */
export function extractRedesSociaisEntryValue(searchArgs: unknown): string {
  const record = searchArgs as Record<string, unknown> | null | undefined;
  const main = record?.[REDES_SOCIAIS_REPORT_TYPE];

  if (typeof main === "string") return main.trim();

  if (main && typeof main === "object" && !Array.isArray(main)) {
    const i = main as Record<string, unknown>;
    if (typeof i.url === "string" && i.url.trim()) return i.url.trim();
    if (typeof i.identifier === "string" && i.identifier.trim()) return i.identifier.trim();
  }
  return "";
}

/**
 * Object fed to the n-gram extractor. For `redes_sociais` it indexes the
 * plaintext entry value (handle/URL) under the `redes_sociais` key — so the
 * report is searchable by its input, exactly like `{cpf: "..."}` — plus the safe
 * `network`/`kind` fields. The encrypted url is never indexed. For other report
 * types the search args pass through unchanged.
 */
export function buildReportSearchArgsNgramInput(
  reportType: string | null | undefined,
  reportSearchArgs: unknown,
): unknown {
  if (isRedesSociaisType(reportType)) {
    const entry = extractRedesSociaisEntryValue(reportSearchArgs);
    const { network, kind } = extractRedesSociaisSafeFields(reportSearchArgs);
    const out: Record<string, string> = {};
    if (entry) out[REDES_SOCIAIS_REPORT_TYPE] = entry;
    if (network) out.network = network;
    if (kind) out.kind = kind;
    return out;
  }
  return reportSearchArgs;
}

/** `input_mode` carried in the display `redes_sociais_ignore` key. */
function readInputMode(searchArgs: unknown): "url" | "identifier" {
  return readExplicitInputMode(searchArgs) ?? "url";
}

/** Same as {@link readInputMode}, but `undefined` when the args don't declare it. */
function readExplicitInputMode(searchArgs: unknown): "url" | "identifier" | undefined {
  const record = searchArgs as Record<string, unknown> | null | undefined;
  const ignore = record?.[REDES_SOCIAIS_IGNORE_KEY];
  if (ignore && typeof ignore === "object" && !Array.isArray(ignore)) {
    const mode = (ignore as Record<string, unknown>).input_mode;
    if (mode === "identifier" || mode === "url") return mode;
  }
  return undefined;
}

/** Fields to patch onto a completed report before re-encryption; empty when
 * there is nothing to fix. */
export interface RedesSociaisCompletionPatch {
  report_name?: string;
  report_search_args?: RedesSociaisDisplaySearchArgs;
}

/** Entry value + metadata recovered from args persisted at creation time. */
export interface RedesSociaisFallback {
  entry: string;
  network: string;
  kind: string;
  inputMode: "url" | "identifier";
}

/**
 * Read the entry value and its metadata out of either search-args shape,
 * deriving `network`/`kind` from the value itself when the args don't carry
 * them. Used to recover a completion envelope that arrived without the entry.
 */
export function extractRedesSociaisFallback(searchArgs: unknown): RedesSociaisFallback {
  const entry = extractRedesSociaisEntryValue(searchArgs);
  const safe = extractRedesSociaisSafeFields(searchArgs);
  const record = searchArgs as Record<string, unknown> | null | undefined;
  const inner = record?.[REDES_SOCIAIS_REPORT_TYPE];

  let inputMode: "url" | "identifier" = readInputMode(searchArgs);
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const i = inner as Record<string, unknown>;
    if (typeof i.url !== "string" && typeof i.identifier === "string") inputMode = "identifier";
  }

  let { network, kind } = safe;
  if (!network || !kind) {
    const classification = classifySocialUrl(entry);
    if (classification.isValid) {
      network = network || classification.network;
      kind = kind || classification.entryKind;
    } else if (entry && !kind) {
      // A bare handle/ID is always a profile entry.
      kind = "perfil";
    }
  }

  return { entry, network, kind, inputMode };
}

function toFallback(fallback: string | RedesSociaisFallback | null | undefined): RedesSociaisFallback {
  if (typeof fallback === "string" || fallback == null) {
    return extractRedesSociaisFallback({ [REDES_SOCIAIS_REPORT_TYPE]: fallback ?? "" });
  }
  return fallback;
}

/**
 * Ensure a completed `redes_sociais` report carries the entry value in its name
 * and search args, and that `redes_sociais_ignore` carries `network`/`kind`.
 *
 * The webhook completion can arrive without the entry (the backend's
 * process-local name cache misses across workers); the client recovers it from
 * the args persisted at creation (`fallback`) and rebuilds the name (preserving
 * the `#<n>` counter). Returns `{}` — nothing to fix — when the type is not
 * `redes_sociais`, or when the envelope already carries the entry AND complete
 * metadata, or when no fallback is available to recover from.
 */
export function reconcileRedesSociaisCompletion(
  report: {
    report_type?: string | null;
    report_name?: string | null;
    report_search_args?: unknown;
  } | null | undefined,
  fallback: string | RedesSociaisFallback | null | undefined,
): RedesSociaisCompletionPatch {
  if (!report || !isRedesSociaisType(report.report_type)) return {};

  // Read the envelope RAW — `extractRedesSociaisFallback` derives the missing
  // metadata, which would hide the very gap being reconciled here.
  const envelopeEntry = extractRedesSociaisEntryValue(report.report_search_args);
  const envelopeSafe = extractRedesSociaisSafeFields(report.report_search_args);
  const envelopeMode = readExplicitInputMode(report.report_search_args);

  if (envelopeEntry && envelopeSafe.network && envelopeSafe.kind) return {};

  const recovered = toFallback(fallback);
  const entry = envelopeEntry || recovered.entry;
  if (!entry) return {};

  const derived = extractRedesSociaisFallback({ [REDES_SOCIAIS_REPORT_TYPE]: entry });
  const network = envelopeSafe.network || recovered.network || derived.network;
  const kind = envelopeSafe.kind || recovered.kind || derived.kind;
  const inputMode = envelopeMode ?? (envelopeEntry ? derived.inputMode : recovered.inputMode);

  const patch: RedesSociaisCompletionPatch = {
    report_search_args: buildRedesSociaisDisplaySearchArgs({
      value: entry,
      network,
      kind,
      inputMode,
    }),
  };

  // The name is only rebuilt when the entry itself was missing from it.
  if (!envelopeEntry) {
    const counter =
      typeof report.report_name === "string"
        ? report.report_name.match(/#\s*(\d+)\s*$/)?.[1]
        : undefined;
    const suffix = counter ? ` #${counter}` : "";
    patch.report_name = `${REDES_SOCIAIS_REPORT_TYPE} ${entry}${suffix}`;
  }

  return patch;
}
